import React from 'react';
import { Box, Card, CardActionArea, CardContent, SvgIcon, Typography } from '@mui/material';

import { useTranslation } from 'react-i18next';
import type { ServiceMode } from './types';

import { Icon } from '@liftango/ui-icons';
import { ICON_FALLBACKS } from '../../utils/iconFallbacks';
import { opsColors } from '../../theme/opsTokens';

interface ServiceModeHubsStepProps {
  serviceMode: ServiceMode | null;
  onSelectMode: (mode: ServiceMode) => void;
  disabled?: boolean;
}

const modeCards: Array<{ id: ServiceMode; titleKey: string; descriptionKey: string; icon: React.ReactNode }> = [
  {
    id: 'door_to_door',
    titleKey: 'serviceMode.doorToDoor.title',
    descriptionKey: 'serviceMode.doorToDoor.description',
    icon: (
      <SvgIcon viewBox="0 0 24 24" sx={{ fontSize: 20 }}>
        <circle cx="5" cy="12" r="2.2" fill="currentColor" />
        <circle cx="19" cy="12" r="2.2" fill="currentColor" />
        <path
          d="M8 12h8M12.8 9.5 16 12l-3.2 2.5"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
      </SvgIcon>
    ),
  },
  {
    id: 'door_to_hub',
    titleKey: 'serviceMode.doorToHub.title',
    descriptionKey: 'serviceMode.doorToHub.description',
    icon: (
      <SvgIcon viewBox="0 0 24 24" sx={{ fontSize: 20 }}>
        <circle cx="5" cy="12" r="2.1" fill="currentColor" />
        <circle cx="13.8" cy="12" r="2.6" fill="currentColor" />
        <circle cx="20" cy="6.8" r="1.4" fill="currentColor" />
        <circle cx="20" cy="12" r="1.4" fill="currentColor" />
        <circle cx="20" cy="17.2" r="1.4" fill="currentColor" />
        <path
          d="M7.6 12h3.2M16.1 10.5 18.6 8.2M16.4 12h2.2M16.1 13.5l2.5 2.3"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
          fill="none"
        />
      </SvgIcon>
    ),
  },
  {
    id: 'hub_to_hub',
    titleKey: 'serviceMode.hubToHub.title',
    descriptionKey: 'serviceMode.hubToHub.description',
    icon: <Icon d={ICON_FALLBACKS.DeviceHub} size={20} />,
  },
];

export default function ServiceModeHubsStep({
  serviceMode,
  onSelectMode,
  disabled = false,
}: ServiceModeHubsStepProps) {
  const { t } = useTranslation('onboarding');
  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="overline" sx={{ color: 'actionBrand.primary', fontWeight: 700 }}>
        {t('serviceMode.overline')}
      </Typography>
      <Typography variant="h4" sx={{ mb: 1.5 }}>
        {t('serviceMode.title')}
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        {t('serviceMode.description')}
      </Typography>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
        {modeCards.map((mode) => {
          const selected = serviceMode === mode.id;
          const title = t(mode.titleKey);
          return (
            <Card
              key={mode.id}
              variant="outlined"
              sx={(theme) => ({
                borderColor: selected ? opsColors.brand.primary : opsColors.neutral.line,
                backgroundColor: selected ? opsColors.neutral.panelAlt : opsColors.neutral.panel,
              })}
            >
              <CardActionArea
                onClick={() => onSelectMode(mode.id)}
                aria-label={t('serviceMode.selectAria', { title })}
                data-testid={`wizard.config.service-mode.${mode.id.replace(/_/g, '-')}`}
                disabled={disabled}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Box
                      sx={(theme) => ({
                        width: 28,
                        height: 28,
                        borderRadius: 1.25,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: selected ? opsColors.brand.primary : opsColors.neutral.panelAlt,
                        color: selected ? '#ffffff' : opsColors.neutral.inkSoft,
                        overflow: 'hidden',
                      })}
                    >
                      {mode.icon}
                    </Box>
                    <Typography variant="h6">{title}</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {t(mode.descriptionKey)}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          );
        })}
      </Box>
    </Box>
  );
}
